import { FC, useState, ChangeEvent, useRef, LegacyRef, useCallback, SetStateAction, Dispatch, useEffect } from 'react';
import { useLocalization } from 'Hooks';
import { Box, Button, Typography } from '@material-ui/core';
import { colors, fontSizes, gutters } from 'Theme/Variables';
import { checkCameraPermission, getBase64ImageType, getImageFromDevice } from 'Utils';
import { BigCirclePlus, CameraIcon, DialogWithActions, PhotoStepPopup } from 'Components';
import ImageCropDialog from 'Components/ImageCropDialog/ImageCropDialog';
import CameraContainer from 'Assets/Images/cameraContainer.png';
import { UsingPhotoDesktop, UsingPhotoMobile } from 'Assets/Images/usingPhoto';
import { localStorageKeys } from 'Config';

import Webcam from 'react-webcam';

import './ChangePhoto.scss';

type ChangePhotoProps = {
  setFileName: Dispatch<SetStateAction<string>>;
  setPhotoData: Dispatch<SetStateAction<string>>;
  setMimType: Dispatch<SetStateAction<string>>;
  setIsChangePhoto: Dispatch<SetStateAction<boolean>>;
};

const ChangePhoto: FC<ChangePhotoProps> = ({ setFileName, setPhotoData, setMimType, setIsChangePhoto }) => {
  const localization = useLocalization() as any;

  const {
    web_card_application_add_photo,
    web_photo_adding_method_pc,
    web_photo_adding_method_webcam,
    web_photo_adding_method_title,
    web_photo_click_to_change,
    web_card_application_click_to_add,
    web_btn_next,
    web_btn_vazgec,
    web_take_photo_btn,
    web_photo_rules_popup_btn,
    web_btn_okay,
    web_photo_rules_link_btn,
  } = localization || {};

  const infoTitle = localization?.web_pc_istanbulcard_apply_page_photo_info_title || 'Bilgilendirme';
  const infoDesc =
    localization?.web_pc_istanbulcard_apply_page_photo_info_desc ||
    'Fotoğrafınızı yüklerken, yüzünüz ve kimliğinizin aynı karede net bir şekilde görünecek şekilde hizalanmış ve uygun boyutlandırılmış olmasına dikkat edin.';

  const webcamRef: any = useRef(null);
  const inputRef: LegacyRef<HTMLInputElement> | undefined = useRef(null);

  const [isOpenPhotoDialog, setIsOpenPhotoDialog] = useState<boolean>(false);
  const [isPermissionGranted, setIsPermissionGrandted] = useState<boolean>(false);
  const [isOpenCamera, setIsOpenCamera] = useState<boolean>(false);
  const [imageType, setImageType] = useState<string>('');
  const [photoSrc, setPhotoSrc] = useState<string | undefined>();
  const [isOpenRuleDialog, setIsOpenRuleDialog] = useState<boolean>(false);
  const [isOpenIntroDialog, setIsOpenIntroDialog] = useState<boolean>(false);
  const [selectedFileName, setSelectedFileName] = useState<string>('');
  const [tempPhotoSrc, setTempPhotoSrc] = useState<string>('');
  const [isOpenCropDialog, setIsOpenCropDialog] = useState<boolean>(false);

  // Component mount olduğunda fotoğraf popup resimlerini önceden yükle
  useEffect(() => {
    const images = [
      ...Object.values(UsingPhotoDesktop),
      ...Object.values(UsingPhotoMobile),
    ];
    images.forEach((src) => {
      const img = new Image();
      img.src = src;
    });
  }, []);

  // Component unmount olduğunda localStorage'ı temizle
  useEffect(() => {
    return () => {
      localStorage.removeItem(localStorageKeys.changePhotoIntroShown);
    };
  }, []);

  const capture = useCallback(() => {
    // Yüksek kalitede screenshot al (quality: 1.0 = maksimum kalite)
    const imageSrc: string = webcamRef?.current?.getScreenshot({
      width: 1920,
      height: 1920,
    });
    setImageType(getBase64ImageType(imageSrc));
    setIsOpenCamera(false);
    setTempPhotoSrc(imageSrc);
    setIsOpenCropDialog(true);
    setSelectedFileName('webcam');
  }, [webcamRef]);

  const getCameraPermission = async () => {
    setIsOpenCamera(true);
    await checkCameraPermission(setIsPermissionGrandted);
  };

  const choosedFromPc = async (event: ChangeEvent<HTMLInputElement>) => {
    const { type, src } = await getImageFromDevice(event);
    setImageType(type);
    if (src !== '') {
      setTempPhotoSrc(src);
      setIsOpenCropDialog(true);
    }

    // Dosya ismini al
    const file = event.target.files?.[0];
    if (file) {
      // Dosya uzantısını kaldır ve sadece ismi al
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
      setSelectedFileName(fileNameWithoutExtension);
    }

    setIsOpenPhotoDialog(false);
    // Input'u resetle - aynı dosya tekrar seçilebilsin
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  };

  const handleCropSave = (croppedImage: string, mimeType: string) => {
    setPhotoSrc(croppedImage);
    // MimeType'ı 'image/jpeg' -> 'jpeg' formatına çevir
    const imageFormat = mimeType.replace('image/', '');
    setImageType(imageFormat);
    setIsOpenCropDialog(false);
  };

  const confirmImage = () => {
    const src = photoSrc?.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
    setFileName(selectedFileName || '');
    setMimType(imageType);
    setPhotoData(src ?? '');
    setIsChangePhoto(false);
  };

  return (
    <div id="changePhoto">
      <Typography variant="h3" align="center">
        <Box mt={gutters.bigger} color={colors.secondary} fontWeight={600}>
          {web_card_application_add_photo}
        </Box>
      </Typography>

      <input onChange={choosedFromPc} ref={inputRef} accept="image/png, image/jpg, image/jpeg" type="file" hidden />
      <div className="changePhoto">
        <div
          onClick={() => {
            if (!isOpenCamera) {
              if (photoSrc) {
                setIsOpenPhotoDialog(true);
              } else {
                // localStorage'dan kontrol et, daha önce gösterildi mi?
                const hasSeenIntro = localStorage.getItem(localStorageKeys.changePhotoIntroShown);
                if (!hasSeenIntro) {
                  setIsOpenIntroDialog(true);
                } else {
                  setIsOpenPhotoDialog(true);
                }
              }
            }
          }}
          tabIndex={0}
          role="button"
          onKeyDown={(e)=>{
            e.preventDefault();
            if (!isOpenCamera) {
              if (photoSrc) {
                setIsOpenPhotoDialog(true);
              } else {
                // localStorage'dan kontrol et, daha önce gösterildi mi?
                const hasSeenIntro = localStorage.getItem(localStorageKeys.changePhotoIntroShown);
                if (!hasSeenIntro) {
                  setIsOpenIntroDialog(true);
                } else {
                  setIsOpenPhotoDialog(true);
                }
              }
            }
          }}
          className="changePhoto__area"
          itemID="updateOpenCameraDialogBtnTest"
        >
          {photoSrc ? (
            <img src={photoSrc} className="changePhoto__img" alt="Yüklenen fotoğraf önizlemesi" />
          ) : !isOpenCamera || !isPermissionGranted ? (
            <>
              <BigCirclePlus />
              <Typography variant="h3" align="center">
                <Box color={colors.darkGray} fontSize={fontSizes.smaller}>
                  {web_card_application_click_to_add}
                </Box>
              </Typography>
            </>
          ) : (
            <div className="cameraContainer">
              <Webcam
                ref={webcamRef}
                videoConstraints={{
                  width: { ideal: 1920 },
                  height: { ideal: 1920 },
                  facingMode: 'user',
                  aspectRatio: 1,
                }}
                screenshotFormat="image/jpeg"
                screenshotQuality={1.0}
                width={200}
                height={200}
                className="webcam"
                audio={false}
              />
              <img src={CameraContainer} alt="" aria-hidden="true" />
            </div>
          )}
        </div>

        {!!photoSrc && (
          <>
            <Typography variant="h3" align="center">
              <Box
                onClick={() => setIsOpenPhotoDialog(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e)=>{
                  if(e.key==='Enter' || e.key===' '){
                    e.preventDefault();
                    setIsOpenPhotoDialog(true);
                  }
                }}
                className="changePhoto__rules"
                mt={gutters.regular}
                mb={gutters.small}
                color={colors.secondary}
                fontSize={fontSizes.tiny}
                itemID="updateChangePhotoBtnTest"
              >
                {web_photo_click_to_change}
              </Box>
            </Typography>
            <Typography variant="h3" align="center">
              <Box
                onClick={() => {
                  setIsOpenRuleDialog(true);
                  // Rule dialog açıldığında da localStorage'dan sil ki tekrar gösterilsin
                  localStorage.removeItem(localStorageKeys.changePhotoIntroShown);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e)=>{
                  if(e.key==='Enter' || e.key===' '){
                    e.preventDefault();
                    setIsOpenRuleDialog(true);
                    localStorage.removeItem(localStorageKeys.changePhotoIntroShown);

                  }
                }}
                className="changePhoto__rules"
                mb={gutters.regular}
                color={colors.primary}
                fontSize={fontSizes.smaller}
                itemID="openPhotoRuleBtnTest"
              >
                {web_photo_rules_link_btn}
              </Box>
            </Typography>
          </>
        )}

        <Box mt={!!photoSrc ? gutters.regular : gutters.big} mb={gutters.regular} className="changePhoto__info">
          <Typography variant="body2">
            <Box fontWeight={600} fontSize={fontSizes.small} mb={0.5} color={colors.secondary}>
              {infoTitle}
            </Box>
          </Typography>
          <Typography variant="body2">
            <Box fontWeight={300} color={colors.checkboxLabel} fontSize={fontSizes.smaller}>
              {infoDesc}
            </Box>
          </Typography>
        </Box>
        {!isOpenCamera || !isPermissionGranted ? (
          <Button
            disabled={!!!photoSrc}
            onClick={() => photoSrc && confirmImage()}
            variant="contained"
            color="secondary"
            fullWidth
            itemID="updateLoadImageBtnTest"
          >
            {web_btn_next}
          </Button>
        ) : (
          <>
            <Button
              startIcon={<CameraIcon />}
              onClick={capture}
              variant="outlined"
              color="secondary"
              fullWidth
              itemID="updateTakePhotoBtnTest"
            >
              {web_take_photo_btn}
            </Button>
            <Box mt={gutters.small} />
            <Button
              style={{ color: 'red' }}
              onClick={() => setIsOpenCamera(false)}
              variant="text"
              color="default"
              fullWidth
              itemID="updateCloseCameraBtnTest"
            >
              {web_btn_vazgec}
            </Button>
          </>
        )}
      </div>
      <DialogWithActions
        alignTitle="left"
        closeIcon
        primaryButton={{
          handleClick: () => {
            inputRef.current?.click();
          },
          label: web_photo_adding_method_pc,
        }}
        secondaryButton={{
          handleClick: () => {
            setPhotoSrc(undefined);
            getCameraPermission();
            setIsOpenPhotoDialog(false);
          },
          label: web_photo_adding_method_webcam,
          type: 'contained',
        }}
        open={isOpenPhotoDialog}
        hide={() => setIsOpenPhotoDialog(false)}
        title={web_photo_adding_method_title}
      />
      <PhotoStepPopup
        isOpen={isOpenIntroDialog || isOpenRuleDialog}
        buttonText={isOpenRuleDialog ? web_btn_okay : web_photo_rules_popup_btn}
        onClick={() => {
          if (isOpenIntroDialog) {
            // Intro dialog kapatılırken localStorage'a kaydet
            localStorage.setItem(localStorageKeys.changePhotoIntroShown, 'true');
          }
          setIsOpenIntroDialog(false);
          if (!isOpenRuleDialog) {
            setIsOpenPhotoDialog(true);
          }
          setIsOpenRuleDialog(false);
        }}
      />
      <ImageCropDialog
        open={isOpenCropDialog}
        hide={() => setIsOpenCropDialog(false)}
        imageSrc={tempPhotoSrc}
        onSave={handleCropSave}
      />
    </div>
  );
};

export default ChangePhoto;
